import { log } from './logger';

/**
 * Utility functions for reservation operations
 */

/**
 * Format reservation details into an email-friendly string
 * @param {Object} reservation - The reservation object
 * @param {Object} venue - The venue object (if not included in reservation)
 * @param {Function} formatDualTimezone - Function to format date/time
 * @returns {string} Formatted reservation details string
 */
export const formatReservationDetailsForEmail = (reservation, venue = null, formatDualTimezone = null) => {
  // Use venue from reservation if available, otherwise use passed venue
  const reservationVenue = reservation.venues || venue;
  
  // Format the reservation date and time
  let formattedDateTime = '';
  if (formatDualTimezone && reservation.reserved_at) {
    try {
      // Use timezone from games if available, otherwise default to Eastern
      const timezone = reservation.games?.timezone || 'America/New_York';
      const dateTime = formatDualTimezone(reservation.reserved_at, timezone);
      formattedDateTime = `${dateTime.date} at ${dateTime.time}`;
    } catch {
      // Error caught and ignored
      // Fallback to simple date formatting
      formattedDateTime = new Date(reservation.reserved_at).toLocaleString();
    }
  } else {
    formattedDateTime = new Date(reservation.reserved_at).toLocaleString();
  }

  // Build the email-friendly string
  let emailContent = `🏒 RESERVATION DETAILS\n`;
  emailContent += `═══════════════════════════════════════\n\n`;
  
  // Basic reservation info
  emailContent += `📋 CONFIRMATION NUMBER: ${reservation.confirmation_number || 'N/A'}\n`;
  emailContent += `📅 DATE & TIME: ${formattedDateTime}\n`;
  emailContent += `👥 GROUP SIZE: ${reservation.group_size} people\n`;
  emailContent += `⏱️ DURATION: ${reservation.duration_hours} hours\n`;
  emailContent += `💰 DEPOSIT PAID: $${reservation.deposit_paid}\n`;
  
  if (reservation.total_cost) {
    emailContent += `💵 TOTAL COST: $${reservation.total_cost}\n`;
  }
  
  emailContent += `📊 STATUS: ${reservation.status.toUpperCase()}\n\n`;

  // Venue information
  if (reservationVenue) {
    emailContent += `🏢 VENUE INFORMATION\n`;
    emailContent += `─────────────────────────────────────\n`;
    emailContent += `Name: ${reservationVenue.name}\n`;
    emailContent += `Address: ${reservationVenue.address_line1}${reservationVenue.address_line2 ? ', ' + reservationVenue.address_line2 : ''}\n`;
    emailContent += `         ${reservationVenue.city}, ${reservationVenue.state} ${reservationVenue.zip_code || ''}\n`;
    
    if (reservationVenue.phone) {
      emailContent += `Phone: ${reservationVenue.phone}\n`;
    }
    if (reservationVenue.website) {
      emailContent += `Website: ${reservationVenue.website}\n`;
    }
    
    // Add cuisine information for restaurants
    if (reservationVenue.venue_categories?.name === 'Restaurant' && reservationVenue.venue_cuisines) {
      const cuisineText = reservationVenue.venue_cuisines.subcategory 
        ? `${reservationVenue.venue_cuisines.category} - ${reservationVenue.venue_cuisines.subcategory}`
        : reservationVenue.venue_cuisines.category;
      emailContent += `Cuisine: ${cuisineText}\n`;
    }
    
    emailContent += `\n`;
  }

  // Team information
  if (reservation.teams) {
    emailContent += `🏒 TEAM INFORMATION\n`;
    emailContent += `─────────────────────────────────────\n`;
    emailContent += `Team: ${reservation.teams.name}\n`;
    emailContent += `\n`;
  }

  // Contact information
  emailContent += `📞 CONTACT INFORMATION\n`;
  emailContent += `─────────────────────────────────────\n`;
  emailContent += `Contact Name: ${reservation.contact_name}\n`;
  emailContent += `Phone: ${reservation.contact_phone}\n`;
  emailContent += `Email: ${reservation.contact_email}\n\n`;

  // Reservation type (specific time vs after game)
  let timeType = reservation.time_type;
  if (!timeType && reservation.notes) {
    try {
      const notesData = JSON.parse(reservation.notes);
      timeType = notesData.time_type;
    } catch {
      // Error caught and ignored
      // Ignore parsing errors
    }
  }

  if (timeType) {
    emailContent += `⏰ RESERVATION TYPE\n`;
    emailContent += `─────────────────────────────────────\n`;
    emailContent += `Type: ${timeType === 'after_game' ? 'After Game' : 'Specific Time'}\n\n`;
  }

  // Game information (if it's an after-game reservation)
  if (timeType === 'after_game' && reservation.games) {
    const game = reservation.games;
    emailContent += `🏆 GAME INFORMATION\n`;
    emailContent += `─────────────────────────────────────\n`;
    
    const homeTeamName = game.home_team?.name || "Home Team";
    const awayTeamName = game.away_team?.name || "Away Team";
    emailContent += `Game: ${homeTeamName} vs ${awayTeamName}\n`;
    
    if (formatDualTimezone && game.starts_at) {
      try {
        const startDateTime = formatDualTimezone(game.starts_at, game.timezone || 'America/New_York');
        const endDateTime = formatDualTimezone(game.ends_at, game.timezone || 'America/New_York');
        emailContent += `Game Date: ${startDateTime.date}\n`;
        emailContent += `Game Time: ${startDateTime.time} to ${endDateTime.time}\n`;
      } catch {
      // Error caught and ignored
        emailContent += `Game Start: ${new Date(game.starts_at).toLocaleString()}\n`;
        emailContent += `Game End: ${new Date(game.ends_at).toLocaleString()}\n`;
      }
    }
    
    if (game.game_locations) {
      emailContent += `Game Location: ${game.game_locations.name}\n`;
      emailContent += `Game Address: ${game.game_locations.address_line1}, ${game.game_locations.city}, ${game.game_locations.state}\n`;
    }
    
    if (game.gamecenter_url) {
      emailContent += `GameCenter: ${game.gamecenter_url}\n`;
    }
    emailContent += `\n`;
  }

  // Special requests, dietary restrictions, and flexible time
  if (reservation.special_requests || reservation.dietary_restrictions || reservation.flexible_time) {
    emailContent += `📝 SPECIAL NOTES\n`;
    emailContent += `─────────────────────────────────────\n`;

    if (reservation.special_requests) {
      emailContent += `Special Requests: ${reservation.special_requests}\n`;
    }

    if (reservation.dietary_restrictions) {
      emailContent += `Dietary Restrictions: ${reservation.dietary_restrictions}\n`;
    }

    if (reservation.flexible_time) {
      emailContent += `Flexible Time: ${reservation.flexible_time}\n`;
    }
    emailContent += `\n`;
  }

  // Footer
  emailContent += `═══════════════════════════════════════\n`;
  emailContent += `This reservation was created through TeamRezy\n`;
  emailContent += `For questions or changes, please contact your venue directly.\n`;

  return emailContent;
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy to clipboard
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Use modern clipboard API
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (err) {
      // Error caught and ignored
    log.error('Failed to copy to clipboard:', err);
    return false;
  }
};